import React, { useMemo } from "react";
import classnames from "classnames";
import stringify from "fast-json-stable-stringify";
import { observer } from "mobx-react";

import { formatNumber } from "@formant/core/common/formatNumber";
import { IFilter } from "@formant/core/model/IFilter";
import { Icon } from "@formant/ui/component/common/Icon";

import { useLiveEventCounts } from "../../utils/useLiveEventCounts";
import { Button } from "./Button";
import styles from "./EventsButton.scss";

export interface IEventsButtonProps {
    filter: IFilter;
    to: string;
    small?: boolean;
    className?: string;
}

export const EventsButton = observer(function EventsButton(
    props: IEventsButtonProps,
) {
    const { filter, to, small, className } = props;

    // filter should never contain names for event count
    const { names, ...filterWithoutNames } = filter;
    const eventFilter = useMemo(
        () => ({
            ...filterWithoutNames,
            viewed: false,
            disableNullMatches: true,
            notificationEnabled: true,
        }),
        [stringify(filterWithoutNames)],
    );

    const counts = useLiveEventCounts(eventFilter);
    const {
        info,
        warning,
        error,
        critical,
        "triggered-event": triggeredEvents,
        "intervention-request": interventions,
        custom,
        stateful,
    } = {
        info: 0,
        warning: 0,
        error: 0,
        critical: 0,
        "triggered-event": 0,
        "intervention-request": 0,
        custom: 0,
        stateful: 0,
        ...counts,
    };

    const notifications = triggeredEvents + interventions + custom + stateful;
    const intervention = interventions > 0;

    const severity =
        critical > 0
            ? "critical"
            : error > 0
              ? "error"
              : warning > 0
                ? "warning"
                : info > 0
                  ? "info"
                  : undefined;

    const visible = !small || notifications > 0;

    return visible ? (
        <Button
            className={classnames(
                styles["events-button"],
                styles.button,
                intervention && styles.intervention,
                severity && styles[severity],
                small && styles.small,
                visible && styles.visible,
                className,
            )}
            to={to}
            data-tooltip="Notifications"
            data-testid="device-events-notification"
        >
            {notifications === 0 && <Icon name="event" />}
            {notifications > 0 && formatNumber(notifications)}
        </Button>
    ) : null;
});
